import { useCallback, useEffect, useRef, useState } from 'react'
import { Button, FullScreenLoader } from '@/components/atoms'
import { useDebouncedAutosave } from '@/hooks/useDebouncedAutosave'
import { AttestationIncompleteError, attestationService } from '@/services/attestationService'
import { authService } from '@/services/authService'
import { organizationService } from '@/services/organizationService'
import {
  getDefaultInstitutionalProfileFormData,
  type InstitutionalProfileFormData,
} from '@/types/institutionalProfile'
import type { KybStatus } from '@/types/kyb'
import type { CreateOrganizationRequest, EmailDomainClaimResult } from '@/types/organization'
import { cn } from '@/utils/cn'
import { DomainClaimStep, type DomainClaimValues } from './DomainClaimStep'
import { EntityAttestationStep, type EntityAttestationValues } from './EntityAttestationStep'
import {
  ALL_PROFILE_FIELD_NAMES,
  NULL_WHEN_EMPTY_FIELDS,
  PHASE_ONE_SECTIONS,
  PROFILE_SECTIONS,
  WRITE_ONLY_FIELDS,
} from './institutionalFields'
import { KybStep } from './KybStep'
import { clearLocalDraft, readLocalDraft, writeLocalDraft } from './localDraft'
import { ProfileStep } from './ProfileStep'
import { ReviewStep } from './ReviewStep'
import { BODY_TEXT, ERROR_TEXT } from './styles'
import { WizardShell, type WizardStepDef } from './WizardShell'

type StepId = 'profile' | 'attestation' | 'details' | 'domain' | 'kyb' | 'review'

/** Which half of onboarding the wizard is running. */
export type SignupPhase = 'one' | 'two'

/**
 * PHASE 1 — the door. Two steps, and only two.
 *
 * 🔴 The contents are constrained by the SERVER, not by taste. Submitting the
 * attestation validates the qualification and compliance booleans, the
 * affirmation, the country, and the incorporation facts (`legal_form`,
 * `incorporation_country`, `incorporation_state`, `registration_number`,
 * `incorporated_on`) — see `MISSING_FIELD_STEP` below, whose keys are the
 * server's `missing_fields` names verbatim. Every one of those is on one of
 * these two steps. Move any of them to phase 2 and the phase-1 submit fails
 * with a 400 naming a field the user was never shown.
 */
const PHASE_ONE_STEPS: readonly WizardStepDef<StepId>[] = [
  { id: 'profile', label: 'Institution' },
  { id: 'attestation', label: 'Entity attestation' },
] as const

/**
 * PHASE 2 — everything the user can do later.
 *
 * 🔴 Order is load-bearing, for the same reason it always was: `kyb` cannot
 * come before the organization exists and holds a `legal_form` (the backend
 * resolves the Sumsub verification level from it, server-side), and the last
 * step is the one that renders the finish button.
 *
 * 🔴 Phase 2 does NOT submit an attestation. The row is already
 * PENDING_VERIFICATION from phase 1, and a GREEN KYB is what promotes it to
 * COMPLETE. Re-submitting here would supersede the pending row for no reason.
 * Phase 2 writes the organization profile (a PATCH), claims the domain, and
 * runs the KYB — nothing else.
 */
const PHASE_TWO_STEPS: readonly WizardStepDef<StepId>[] = [
  { id: 'details', label: 'Institution details' },
  { id: 'domain', label: 'Email domain' },
  { id: 'kyb', label: 'Verification' },
  { id: 'review', label: 'Review & finish' },
] as const

/** Autosave quiet period. Long enough that typing a field is one request, not ten. */
const AUTOSAVE_DELAY_MS = 1500

/** The org cannot be created without a name, so autosave waits for a usable one. */
const MIN_NAME_LENGTH = 2

/**
 * Where each name from a 400 `missing_fields` list belongs.
 *
 * 🔴 Every entry maps to a PHASE 1 step. That is the invariant that makes
 * phase 1 submittable on its own: if the server can ever ask for a field, the
 * field has to be somewhere the user has already been. A new required field
 * added to phase 2 would send `setStep` at a step id that phase 1 does not
 * contain, and the error would render nowhere.
 */
const MISSING_FIELD_STEP: Record<string, StepId> = {
  affirmation: 'attestation',
  country: 'attestation',
  attestation_year: 'attestation',
  email: 'attestation',
  accredited_investor_status: 'attestation',
  qualified_institutional_buyer: 'attestation',
  institutional_customer: 'attestation',
  anti_money_laundering_compliance: 'attestation',
  know_your_customer_compliance: 'attestation',
  sanctions_compliance: 'attestation',
  data_protection_compliance: 'attestation',
  institutional_profile: 'profile',
  __qualification: 'attestation',
  // The incorporation facts live on the profile step next to tax_id_ein. The
  // same names also arrive via the KYB 409 (`kyb_level_unresolved`), which the
  // KYB step routes to the profile step itself.
  legal_form: 'profile',
  incorporation_country: 'profile',
  incorporation_state: 'profile',
  registration_number: 'profile',
  incorporated_on: 'profile',
}

const MISSING_FIELD_COPY: Record<string, string> = {
  affirmation: 'Tick the affirmation to submit on behalf of the entity.',
  country: 'Enter the country the entity is incorporated in.',
  attestation_year:
    'The attestation year on your saved draft is out of range. Reload the page and try again.',
  email: 'We could not determine the signing email for your account. Reload and try again.',
  anti_money_laundering_compliance: 'Confirm the AML attestation.',
  know_your_customer_compliance: 'Confirm the KYC attestation.',
  sanctions_compliance: 'Confirm the sanctions attestation.',
  data_protection_compliance: 'Confirm the data-protection attestation.',
  institutional_profile: 'Complete the institution profile before submitting.',
  __qualification:
    'Select at least one qualification category — accredited investor, QIB, or institutional customer.',
  legal_form: "Select the organization's legal form — it decides which verification checks run.",
  incorporation_country: 'Select the country the entity is incorporated in.',
  incorporation_state: 'Select the state or province of incorporation.',
  registration_number: "Enter the entity's registration number.",
  incorporated_on: 'Enter the date of incorporation.',
}

/**
 * Turn `missing_fields` into inline errors keyed by control.
 *
 * The one shape that is not a plain field name is the qualification
 * requirement, which arrives as `"a or b or c"` because the entity needs any one
 * of the three and the server cannot know which was intended. It is folded onto
 * the synthetic `__qualification` key that the step renders above the group.
 */
const mapMissingFields = (missingFields: string[]): Record<string, string> => {
  const errors: Record<string, string> = {}
  for (const entry of missingFields) {
    const key = entry.includes(' or ') ? '__qualification' : entry
    errors[key] =
      MISSING_FIELD_COPY[key] || `This is required before you can submit (${entry}).`
  }
  return errors
}

const defaultAttestation = (): EntityAttestationValues => ({
  country: '',
  affirmation: false,
  subscribe_to_newsletter: true,
})

/**
 * Everything writable about the profile, plus the org name.
 *
 * 🔴 A write-only field with an empty value is OMITTED, never sent as ''. The
 * API never returns those two values, so on resume the box is legitimately empty
 * while the server holds a real number — sending the empty string would wipe a
 * bank account number the user never saw and could not have re-typed.
 */
const buildProfilePatch = (
  profile: InstitutionalProfileFormData,
): Partial<CreateOrganizationRequest> => {
  const patch: Record<string, unknown> = {
    name: profile.institution_name.trim() || 'My Organization',
  }
  for (const key of ALL_PROFILE_FIELD_NAMES) {
    const value = profile[key]
    if (WRITE_ONLY_FIELDS.includes(key) && (typeof value !== 'string' || !value.trim())) {
      continue
    }
    // Server-validated fields must never receive the form's '' default — see
    // NULL_WHEN_EMPTY_FIELDS. Null means "not provided" and clears cleanly.
    patch[key] = NULL_WHEN_EMPTY_FIELDS.includes(key) && value === '' ? null : value
  }
  return patch as Partial<CreateOrganizationRequest>
}

interface InstitutionalSignupScreenProps {
  /**
   * Which half to run.
   *
   * `one` for a user with no submitted attestation; `two` for one whose
   * attestation is already PENDING_VERIFICATION. `AttestationScreen` decides
   * from the server's `pending_attestation`, never from localStorage.
   */
  phase: SignupPhase
  /** Called once the attestation reaches COMPLETE and the user may enter the app. */
  onComplete: () => void
  /**
   * Phase 1 submitted and came back PENDING_VERIFICATION — the normal path.
   * The user has earned the `browse` tier and goes into the app; phase 2 is
   * offered from there.
   */
  onPhaseOneComplete: () => void
  /** Phase 2 finished (or the user chose to stop). Back into the app. */
  onPhaseTwoDone: () => void
}

/**
 * Institutional signup, end to end.
 *
 * ## Why this exists
 * The previous implementation created an organization and no attestation at all,
 * while `ProtectedRoute` gates on `GET /attestations/status`. Every institutional
 * signup therefore looped: create org → land on /profile-preferences → bounced
 * back to /attestation, forever. This flow always terminates in a submitted
 * attestation.
 *
 * ## How progress is saved
 * The ~70 profile fields cannot live on the attestation draft — `PUT
 * /attestations/draft` accepts only the attestation-row fields and silently
 * drops everything else (verified against the running server). So the server-side
 * draft for an institutional signup is the *pair*:
 *
 *   - the organization + its institutional profile (`POST /organizations`, then
 *     `PATCH /organizations/{id}`), and
 *   - the entity attestation draft (`PUT /attestations/draft` with
 *     `organization_id`).
 *
 * Creating the organization early is safe *because* of the state machine: an org
 * posted without attestation answers gets a DRAFT attestation, and a DRAFT
 * satisfies no gate and grants nothing. Both writes happen behind one debounced,
 * serialized autosave.
 *
 * ## StrictMode
 * `POST /organizations` is the one call here that is NOT idempotent — running it
 * twice mints two organizations. React StrictMode double-invokes effects in
 * development, and this codebase has already shipped that bug twice. Two refs
 * guard it: `createOrgRef` holds the in-flight promise (so a second caller awaits
 * the first rather than starting another) and survives the remount, while
 * `mountedRef` is tracked separately because an effect-scoped flag is already
 * false when the in-flight request resolves and would drop the result.
 */
export const InstitutionalSignupScreen = ({
  phase: signupPhase,
  onComplete,
  onPhaseOneComplete,
  onPhaseTwoDone,
}: InstitutionalSignupScreenProps) => {
  const isPhaseOne = signupPhase === 'one'
  const STEPS = isPhaseOne ? PHASE_ONE_STEPS : PHASE_TWO_STEPS

  const [uiPhase, setUiPhase] = useState<'loading' | 'form'>('loading')
  const [step, setStep] = useState<StepId>(STEPS[0].id)

  const [profile, setProfile] = useState<InstitutionalProfileFormData>(
    getDefaultInstitutionalProfileFormData,
  )
  const [attestation, setAttestation] = useState<EntityAttestationValues>(defaultAttestation)
  const [domainValues, setDomainValues] = useState<DomainClaimValues>({
    email_domain: '',
    verification_email: '',
  })

  const [claim, setClaim] = useState<EmailDomainClaimResult | null>(null)
  const [claiming, setClaiming] = useState(false)
  const [claimError, setClaimError] = useState<string | null>(null)

  const [bankOnFile, setBankOnFile] = useState<Record<string, boolean>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)

  const accountEmail = authService.getUser()?.email || ''

  // --- refs that must survive a StrictMode remount -------------------------
  const mountedRef = useRef(true)
  const hydratedRef = useRef(false)
  const orgIdRef = useRef<string | null>(null)
  const createOrgRef = useRef<Promise<string> | null>(null)
  const attestationIdRef = useRef<string | null>(null)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  /**
   * The organization id, creating the organization on first need.
   *
   * Every caller funnels through the same promise: concurrent callers await the
   * one in flight instead of starting a second POST, and the ref outlives the
   * StrictMode remount so the second mount never creates a duplicate. Only a
   * *failed* create clears the ref, so a retry is possible.
   */
  const ensureOrganization = useCallback(
    async (current: InstitutionalProfileFormData): Promise<string> => {
      if (orgIdRef.current) return orgIdRef.current
      if (createOrgRef.current) return createOrgRef.current

      // 🔴 PHASE 2 MAY NEVER CREATE AN ORGANIZATION.
      //
      // Reaching phase 2 means the server reported a PENDING_VERIFICATION
      // attestation, which only exists because an organization does. So a null
      // `orgIdRef` here does not mean "no organization yet" — it means the
      // resume failed to find it (`listOrganizations` errored, or the caller
      // is not an OWNER/ADMIN of it). POSTing would mint a SECOND organization
      // for someone who already has one, and the new one would carry none of
      // the verification the first one is waiting on.
      if (!isPhaseOne) {
        throw new Error(
          'We could not load your organization. Reload the page and try again.',
        )
      }

      const pending = (async () => {
        const org = await organizationService.createOrganization(
          buildProfilePatch(current) as CreateOrganizationRequest,
        )
        orgIdRef.current = org.id
        // The server-side draft now owns this data; a second copy in localStorage
        // could only go stale and fight it on the next resume.
        clearLocalDraft()

        // `create_organization` seeds the draft's country from `mailing_country`.
        // Adopt it rather than immediately overwriting it with an empty string.
        const draft = await attestationService.getDraft(org.id)
        if (draft) {
          attestationIdRef.current = draft.attestation_id
          if (mountedRef.current && draft.country) {
            setAttestation(prev => (prev.country ? prev : { ...prev, country: draft.country ?? '' }))
          }
        }
        return org.id
      })()

      createOrgRef.current = pending
      try {
        return await pending
      } catch (error) {
        createOrgRef.current = null
        throw error
      }
    },
    [],
  )

  /**
   * The wizard no longer owns a terminal "pending" phase.
   *
   * It used to submit, discover PENDING_VERIFICATION, and render
   * `KybPendingPanel` itself — because a pending attestation could not get
   * into the app and there was nowhere else to send the user. Now it can:
   * phase 1's submit hands off to `onPhaseOneComplete`, the access tier
   * refreshes, and the user lands on the dashboard with financial data gated.
   * `AttestationScreen` owns what a returning pending user sees.
   */

  // --- resume --------------------------------------------------------------
  useEffect(() => {
    if (hydratedRef.current) return
    hydratedRef.current = true

    const hydrate = async () => {
      try {
        const memberships = await organizationService.listOrganizations()
        const owned = memberships.find(
          entry => entry.role === 'OWNER' || entry.role === 'ADMIN',
        )

        if (!owned) {
          // Nothing on the server yet — fall back to the pre-organization mirror.
          const local = readLocalDraft()
          if (local && mountedRef.current) {
            setProfile(prev => ({ ...prev, ...local }))
          }
          if (mountedRef.current) setUiPhase('form')
          return
        }

        orgIdRef.current = owned.organization.id
        const org = await organizationService.getOrganization(owned.organization.id)
        const stored = org.institutional_profile ?? {}

        if (mountedRef.current) {
          setProfile(prev => {
            const next = { ...prev }
            for (const key of ALL_PROFILE_FIELD_NAMES) {
              const value = (stored as Record<string, unknown>)[key]
              // Absent fields come back as null, and the write-only pair never
              // comes back at all — leave those at their empty defaults.
              if (value === null || value === undefined) continue
              if (typeof value === typeof next[key]) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ;(next as any)[key] = value
              }
            }
            if (!next.institution_name) next.institution_name = org.name
            return next
          })
          setBankOnFile({
            bank_account_number: !!stored.bank_account_number_set,
            bank_routing_number: !!stored.bank_routing_number_set,
          })
          if (org.email_domain) {
            setDomainValues(prev => ({ ...prev, email_domain: org.email_domain ?? '' }))
            setClaim({
              email_domain: org.email_domain,
              email_domain_verified: org.email_domain_verified,
              email_domain_verified_at: org.email_domain_verified_at,
              auto_verified: false,
              verification_email_sent: false,
            })
          }
        }

        // 🔴 PHASE 2 NEVER TOUCHES THE ATTESTATION DRAFT.
        //
        // A phase-2 user's attestation is already submitted and sitting in
        // PENDING_VERIFICATION, so `GET /attestations/draft` answers null for
        // them by definition. Calling `saveDraft` here — which is what the
        // single-phase flow did to "re-open" a draft — would mint a fresh
        // DRAFT row alongside the pending one, and submitting that would
        // SUPERSEDE the submission the user is currently waiting on. Phase 2's
        // job is the organization PATCH, the domain claim and the KYB; the
        // attestation is finished business.
        if (!isPhaseOne) {
          if (mountedRef.current) {
            clearLocalDraft()
            setUiPhase('form')
          }
          return
        }

        let draft = await attestationService.getDraft(org.id)

        if (!draft) {
          // No DRAFT and we are in phase 1, which `AttestationScreen` only
          // chooses when the server reports NO pending attestation. So this is
          // a user whose previous row is COMPLETE, EXPIRED or SUPERSEDED and
          // who is legitimately starting again. Re-open a draft so the flow
          // has something to submit; submitting supersedes rather than
          // duplicates.
          draft = await attestationService.saveDraft({ organization_id: org.id })
        }

        attestationIdRef.current = draft.attestation_id
        if (mountedRef.current) {
          setAttestation({
            country: draft.country ?? '',
            affirmation: draft.affirmation,
            subscribe_to_newsletter: draft.subscribe_to_newsletter,
          })
          clearLocalDraft()
          setUiPhase('form')
        }
      } catch (error) {
        if (!mountedRef.current) return
        setLoadError(
          error instanceof Error ? error.message : 'Could not load your saved progress',
        )
        setUiPhase('form')
      }
    }

    void hydrate()
    // `isPhaseOne` is fixed for the lifetime of a mount: `AttestationScreen`
    // keys this component on the phase, so a phase change remounts it and
    // re-runs the hydrate from scratch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // --- autosave ------------------------------------------------------------
  const canAutosave =
    uiPhase === 'form' && profile.institution_name.trim().length >= MIN_NAME_LENGTH

  const persist = useCallback(
    async (snapshot: { profile: InstitutionalProfileFormData; attestation: EntityAttestationValues }) => {
      const orgId = await ensureOrganization(snapshot.profile)
      await organizationService.updateOrganization(orgId, buildProfilePatch(snapshot.profile))

      // 🔴 Phase 2 writes the organization and stops. Writing the attestation
      // draft here would create a second DRAFT row beside the submitted,
      // PENDING_VERIFICATION one, and submitting that would supersede the
      // submission the user is waiting on. See the resume effect above.
      if (!isPhaseOne) return

      const draft = await attestationService.saveDraft({
        organization_id: orgId,
        country: snapshot.attestation.country,
        affirmation: snapshot.attestation.affirmation,
        subscribe_to_newsletter: snapshot.attestation.subscribe_to_newsletter,
      })
      attestationIdRef.current = draft.attestation_id
    },
    [ensureOrganization, isPhaseOne],
  )

  const autosave = useDebouncedAutosave({
    value: { profile, attestation },
    save: persist,
    delay: AUTOSAVE_DELAY_MS,
    enabled: canAutosave,
  })

  // Pre-organization safety net. Only runs while there is no server-side row to
  // hold the answers; once the organization exists this stops and the mirror is
  // deleted, so there is never a question of which copy wins on resume.
  useEffect(() => {
    if (uiPhase !== 'form') return
    if (orgIdRef.current) return
    writeLocalDraft(profile)
  }, [profile, uiPhase])

  // Every step is a different height and the card has no fixed height, so the
  // browser keeps the previous scroll offset across a step change: clicking
  // Continue at the bottom of the long profile step used to leave the viewport
  // part-way down a much shorter one, which reads as the page jumping. It also
  // matters on the submit path, where a validation failure sends the user to
  // another step whose error would otherwise be off-screen. Instant, not
  // smooth — a step change is a page change, not a scroll.
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' })
  }, [step])

  // --- handlers ------------------------------------------------------------
  const handleProfileChange = useCallback(
    (name: keyof InstitutionalProfileFormData, value: string | boolean) => {
      setProfile(prev => {
        const next = { ...prev, [name]: value }
        // The state/province options belong to the selected country; a code
        // left over from the previous country (e.g. `DE` after USA → GBR)
        // would be saved invisibly and sent to the registry check.
        if (name === 'incorporation_country' && value !== prev.incorporation_country) {
          next.incorporation_state = ''
        }
        return next
      })
      setFieldErrors(prev => {
        if (!prev[name] && !prev.__qualification && !prev.__submit) return prev
        const next = { ...prev }
        delete next[name]
        delete next.__submit
        // Any qualification tick can satisfy the group requirement.
        if (
          name === 'accredited_investor_status' ||
          name === 'qualified_institutional_buyer' ||
          name === 'institutional_customer'
        ) {
          delete next.__qualification
        }
        return next
      })
    },
    [],
  )

  const handleAttestationChange = useCallback((updates: Partial<EntityAttestationValues>) => {
    setAttestation(prev => ({ ...prev, ...updates }))
    setFieldErrors(prev => {
      const next = { ...prev }
      for (const key of Object.keys(updates)) delete next[key]
      delete next.__submit
      return next
    })
  }, [])

  const handleClaimDomain = useCallback(async () => {
    setClaiming(true)
    setClaimError(null)
    try {
      // The domain is claimed against the organization, so it has to exist first.
      const orgId = await ensureOrganization(profile)
      const result = await organizationService.claimEmailDomain(orgId, {
        email_domain: domainValues.email_domain.trim().replace(/^@/, ''),
        verification_email: domainValues.verification_email.trim() || undefined,
      })
      if (mountedRef.current) setClaim(result)
    } catch (error) {
      if (mountedRef.current) {
        setClaimError(error instanceof Error ? error.message : 'Could not claim the domain')
      }
    } finally {
      if (mountedRef.current) setClaiming(false)
    }
  }, [domainValues, ensureOrganization, profile])

  /**
   * The KYB step's org resolver — the `handleClaimDomain` pattern: the
   * applicant is created against the organization, so it must exist first.
   * The flush matters: a `legal_form` picked seconds ago can still be sitting
   * in the 1500ms autosave debounce, and the backend resolves the verification
   * level from what the SERVER holds — minting before the write lands would
   * 409 on a value the user has already chosen.
   */
  const resolveKybOrg = useCallback(async () => {
    await autosave.flush()
    return ensureOrganization(profile)
  }, [autosave, ensureOrganization, profile])

  /**
   * The KYB step reports that a Sumsub applicant now exists.
   *
   * 🔴 Nothing is gated on this any more, and that is deliberate. The single-
   * phase flow disabled Continue until a token had been issued, because the
   * next step was the submit and submitting without an applicant produced an
   * attestation that could never complete. Phase 2 has no submit — the
   * attestation went in at the end of phase 1 — so the same guard would only
   * trap the users who need it least: someone whose KYB is already `approved`
   * or under review may get no fresh token, and would have been unable to
   * reach the last step at all.
   */
  const handleKybTokenIssued = useCallback((_status: KybStatus) => {}, [])

  /**
   * 409 `kyb_level_unresolved` recovery: back to the profile fields, field
   * marked.
   *
   * 🔴 This is why phase 2's `details` step renders the institution section
   * too, not only the six deferred ones. `legal_form` decides the Sumsub
   * verification level, the KYB step is in phase 2, and a 409 has to land the
   * user on a control they can actually change. Phase 2 owning the whole
   * profile is what makes that reachable — and it costs the user nothing,
   * because those fields are already filled in from phase 1.
   */
  const handleKybEditProfile = useCallback(() => {
    setFieldErrors(prev => ({ ...prev, legal_form: MISSING_FIELD_COPY.legal_form }))
    setStep(isPhaseOne ? 'profile' : 'details')
  }, [isPhaseOne])

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)
    setFieldErrors({})
    try {
      // Anything typed in the last second-and-a-half is still sitting in the
      // debounce. Submitting before it lands would validate a stale draft.
      await autosave.flush()

      const attestationId = attestationIdRef.current
      if (!attestationId) {
        throw new Error('Your progress has not been saved yet. Wait a moment and try again.')
      }

      const result = await attestationService.submitDraft(attestationId)
      clearLocalDraft()

      if (result.status === 'PENDING_VERIFICATION') {
        // Submitted, but verification — the domain challenge, the KYB review,
        // or both — is outstanding. It does NOT open the full gate, but since
        // the two-phase redesign it does open the door: the user has finished
        // phase 1 and earns the `browse` tier, so the parent refreshes the
        // access tier and sends them into the app with financial data gated.
        //
        // This is the normal, expected outcome of a phase-1 submit — an
        // organization submit always lands in PENDING_VERIFICATION today, and
        // only a GREEN KYB promotes it to COMPLETE.
        onPhaseOneComplete()
        return
      }
      // COMPLETE straight away. Rare for an organization, but the state
      // machine allows it, and it means full access with nothing outstanding.
      onComplete()
    } catch (error) {
      if (!mountedRef.current) return
      if (error instanceof AttestationIncompleteError) {
        const errors = mapMissingFields(error.missingFields)
        setFieldErrors(errors)
        const target = Object.keys(errors)
          .map(key => MISSING_FIELD_STEP[key])
          .find(Boolean)
        if (target) setStep(target)
        return
      }
      setFieldErrors({
        __submit: error instanceof Error ? error.message : 'Could not submit your attestation',
      })
    } finally {
      if (mountedRef.current) setSubmitting(false)
    }
  }, [autosave, onComplete, onPhaseOneComplete])

  /**
   * Phase 2's finish button.
   *
   * Nothing is submitted: the profile PATCH, the domain claim and the KYB have
   * each already been written by the step that owns them. This just flushes
   * anything still in the autosave debounce and hands back to the parent.
   */
  const handleFinishPhaseTwo = useCallback(async () => {
    setSubmitting(true)
    // 🔴 `flush()` RESOLVES on failure — `useDebouncedAutosave.runSave` catches
    // every rejection and records it in `state`, and says so in its own
    // docstring. A `try/catch` here is dead code that would navigate the user
    // out of the wizard as if the final save had landed. Read the state.
    const saved = await autosave.flush()
    if (!mountedRef.current) return
    if (!saved) {
      setFieldErrors({
        __submit:
          'Your last change could not be saved. Check your connection and try again — nothing already saved is lost.',
      })
      setSubmitting(false)
      return
    }
    onPhaseTwoDone()
  }, [autosave, onPhaseTwoDone])

  // --- render --------------------------------------------------------------
  if (uiPhase === 'loading') {
    return <FullScreenLoader text="Loading your institutional profile..." />
  }

  const stepIndex = STEPS.findIndex(entry => entry.id === step)
  // 🔴 Position, not a hardcoded step id. It used to be `step === 'review'`,
  // which silently made any step appended after review unreachable-forward.
  // With two different step lists that trap would fire on the first edit.
  const isLastStep = stepIndex === STEPS.length - 1
  const nameTooShort = profile.institution_name.trim().length < MIN_NAME_LENGTH

  const footer = (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div>
        {stepIndex > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() => setStep(STEPS[stepIndex - 1].id)}
            disabled={submitting}
          >
            Back
          </Button>
        )}
      </div>
      <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
        {step === 'profile' && nameTooShort && (
          <span className={cn(BODY_TEXT, 'sm:mr-2')}>
            Enter the institution name to start saving your progress.
          </span>
        )}
        {isLastStep ? (
          <Button
            type="button"
            variant="primary"
            onClick={isPhaseOne ? handleSubmit : handleFinishPhaseTwo}
            isLoading={submitting}
            // 🔴 The name only blocks PHASE 1. Phase 2 submits nothing, and a
            // hydrate that failed to populate the organization name would
            // otherwise disable the only way out of the wizard.
            disabled={submitting || (isPhaseOne && nameTooShort)}
          >
            {isPhaseOne ? 'Submit and continue' : 'Finish'}
          </Button>
        ) : (
          <Button
            type="button"
            variant="primary"
            onClick={() => setStep(STEPS[stepIndex + 1].id)}
            disabled={submitting || (step === 'profile' && nameTooShort)}
          >
            Continue
          </Button>
        )}
        {/* Phase 2 is resumable and none of it blocks browsing, so there is
            always a way back to the app. Without this a user who opened phase
            2 to look at it would be stuck in the wizard. */}
        {!isPhaseOne && (
          <Button
            type="button"
            variant="outline"
            onClick={onPhaseTwoDone}
            disabled={submitting}
          >
            Save and continue later
          </Button>
        )}
      </div>
    </div>
  )

  return (
    <WizardShell
      steps={STEPS}
      currentStep={step}
      onStepSelect={setStep}
      title={isPhaseOne ? 'Institutional account' : 'Finish verifying your institution'}
      subtitle={
        isPhaseOne
          ? 'Two short steps: tell us about the entity, and attest on its behalf. You can start looking around SecondLane straight afterwards — the rest of verification can wait.'
          : 'The remaining details, your email domain, and the business verification itself. Your progress is saved as you go — leave and come back whenever you like.'
      }
      saveState={autosave.state}
      saveError={autosave.error}
      lastSavedAt={autosave.lastSavedAt}
      footer={footer}
    >
      {loadError && <p className={cn(ERROR_TEXT, 'mb-4')}>{loadError}</p>}

      {/* Phase 1's block A: the institution's identity, and nothing else. */}
      {step === 'profile' && (
        <ProfileStep
          sections={PHASE_ONE_SECTIONS}
          profile={profile}
          onChange={handleProfileChange}
          errors={fieldErrors}
          onFile={bankOnFile}
          disabled={submitting}
        />
      )}

      {/* Phase 2's details step shows the WHOLE profile, not only the six
          deferred sections. The institution block is already filled in from
          phase 1, so it costs nothing to scroll past — and it is the only
          place a phase-2 user can correct a `legal_form` that the KYB step
          rejected with a 409. See `handleKybEditProfile`. */}
      {step === 'details' && (
        <ProfileStep
          sections={PROFILE_SECTIONS}
          profile={profile}
          onChange={handleProfileChange}
          errors={fieldErrors}
          onFile={bankOnFile}
          disabled={submitting}
        />
      )}

      {step === 'attestation' && (
        <EntityAttestationStep
          profile={profile}
          attestation={attestation}
          onProfileChange={handleProfileChange}
          onAttestationChange={handleAttestationChange}
          errors={fieldErrors}
          signerEmail={accountEmail}
          disabled={submitting}
        />
      )}

      {step === 'domain' && (
        <DomainClaimStep
          values={domainValues}
          onChange={updates => setDomainValues(prev => ({ ...prev, ...updates }))}
          claim={claim}
          onClaim={handleClaimDomain}
          claiming={claiming}
          error={claimError}
          accountEmail={accountEmail}
          disabled={submitting}
        />
      )}

      {step === 'kyb' && (
        <KybStep
          resolveOrgId={resolveKybOrg}
          onTokenIssued={handleKybTokenIssued}
          onEditProfile={handleKybEditProfile}
          disabled={submitting}
        />
      )}

      {step === 'review' && (
        <ReviewStep
          profile={profile}
          claim={claim}
          onProfileChange={handleProfileChange}
          errors={fieldErrors}
          disabled={submitting}
        />
      )}
    </WizardShell>
  )
}
